import { mpClient } from "../multiplayer/client";
import {
  SoccerSim,
  SOCCER_TEAM_BLUE,
  SOCCER_TEAM_ORANGE,
  assignRandomTeams,
  parseSoccerVariant,
  separateCursorFromBall,
  soccerVariantConfig,
  type SoccerPlayerInput,
  type SoccerSnapshot,
  type SoccerTeam,
  type SoccerVariantId,
} from "../../../lib/multiplayer/soccer-sim";
import type { MpServerMessage, SoccerFormat, SoccerStateWire } from "../../../lib/multiplayer/protocol";
import { computeLocalZones } from "../local/zones";
import type { LocalZoneRect } from "../local/types";
import {
  playSoccerCountdown,
  playSoccerGoal,
  playSoccerHit,
  playSoccerMessage,
  playSoccerPickupMax,
  playSoccerPickupSmall,
  playSoccerPost,
  playSoccerTimeWarn,
  playSoccerWall,
  resumeSoccerAudio,
} from "./audio";
import {
  renderSoccerBall,
  renderSoccerBoostHint,
  renderSoccerBoostRing,
  renderSoccerField,
  renderSoccerHud,
  renderSoccerPickups,
  renderSoccerReplaySkip,
} from "./render";
import {
  hideOverlay,
  hideSoccerMenuBtn,
  hideSoccerScreens,
  renderFormatSelect,
  renderSoccerHostVariant,
  renderSoccerPause,
  renderSoccerQueue,
  renderSoccerWinnerThenResults,
  renderTeamSelect,
  renderVariantSelect,
  setSoccerRematchHint,
  showSoccerMenuBtn,
  showSoccerReplaySkipBtn,
  hideSoccerReplaySkipBtn,
  showSoccerToast,
  updateSoccerQueueCount,
  type SoccerWinnerCard,
} from "./ui";
import {
  computeSoccerView,
  isSoccerTouchBoostUi,
  screenToWorld,
  soccerScale,
  worldToScreen,
  worldToViewNorm,
  type SoccerView,
} from "./view";

export type SoccerRuntimeHooks = {
  initGame: (mode: string) => void;
  showEl: (el: HTMLElement | null) => void;
  hideEl: (el: HTMLElement | null) => void;
  getCanvasSize: () => { w: number; h: number };
  getMouse: () => { x: number; y: number };
  setMouse: (x: number, y: number) => void;
  isPlaying: () => boolean;
  stopPlaying: () => void;
  drawCursorAt: (ctx: CanvasRenderingContext2D, x: number, y: number, color: string) => void;
  drawPlayerName: (ctx: CanvasRenderingContext2D, x: number, y: number, name: string, color: string) => void;
  getPlayerName: () => string;
  getAccountUserId?: () => string | null;
  getDots: () => number;
  getTournamentsWon: () => number;
  getCursorColor: () => string;
  getEquippedCosmeticIds: () => string[];
  getProfilePhotoUrl?: () => string | null;
  getBannerUrl?: () => string | null;
  openGameHub: () => void;
  openMpEntry: () => void;
  openLocalSetup: () => void;
  playModeCardBeep: () => void;
  hideReturn: () => void;
  convertClientDelta?: (dx: number, dy: number) => { dx: number; dy: number };
};

let hooks: SoccerRuntimeHooks | null = null;
let active = false;
let origin: "compete" | "multiplayer" | "local" = "compete";
let localSim: SoccerSim | null = null;
let netSnap: SoccerSnapshot | null = null;
let localTeam: SoccerTeam = "blue";
let lastGoalId = 0;
let lastMsgKey = "";
let lastBanner = "";
let lastCountdownShown = "";
let localPaused = false;
let resumeToken = "";
let resumePlayerId = "";
let reconnectTimer: ReturnType<typeof setInterval> | null = null;
let queueFormat: SoccerFormat | null = null;
let queueVariant: SoccerVariantId | null = null;
let activeVariant: SoccerVariantId = "normal";
let localDraftVariant: SoccerVariantId = "normal";
let pcBoostHeld = false;
let mobileBoostOn = false;
let boostWireActive = false;
let boostBtn: HTMLButtonElement | null = null;
let boostListeners: (() => void) | null = null;
let unsub: (() => void) | null = null;

type LocalP = {
  id: string;
  name: string;
  team: SoccerTeam;
  x: number;
  y: number;
  /** MEGA: stick/mouse target the sim chases (ice slide). */
  aimX: number;
  aimY: number;
  vx: number;
  vy: number;
  prevX: number;
  prevY: number;
  lastMoveAt: number;
  zone: LocalZoneRect;
};
let localPlayers: LocalP[] = [];
let localZones: LocalZoneRect[] = [];
const steer = new Map<number, { index: number; lastX: number; lastY: number }>();
let touchRoot: HTMLElement | null = null;
let localDraftTeams: SoccerTeam[] = [];
let localDraftNames: string[] = [];
let endedShown = false;
let replaySkipSent = false;
let localReplayAwaiting: string[] = [];
let lastSnapPhase: SoccerSnapshot["phase"] | "" = "";
let lastKickoffSyncedGoal = 0;
let snapLerp = 1;
let snapLerpFrom: SoccerSnapshot["players"] | null = null;

const STATS_KEY = "soccerStatsV1";

export function initSoccerRuntime(h: SoccerRuntimeHooks): void {
  hooks = h;
  if (!unsub) {
    unsub = mpClient.onMessage(onMpMessage);
  }
}

export function isSoccerActive(): boolean {
  return active;
}

export function isSoccerLocal(): boolean {
  return active && origin === "local";
}

export function soccerBlocksReturn(): boolean {
  return active && !endedShown;
}

export function beginSoccerCompeteQueue(fmt: SoccerFormat, variant: SoccerVariantId = "normal"): void {
  if (!hooks) return;
  queueFormat = fmt;
  queueVariant = variant;
  activeVariant = variant;
  origin = "compete";
  resumeSoccerAudio();
  void (async () => {
    try {
      if (!mpClient.connected) await mpClient.connect();
      mpClient.send({
        type: "soccer_queue",
        format: fmt,
        variant,
        name: hooks!.getPlayerName() || "Player",
        accountId: hooks!.getAccountUserId?.() ?? undefined,
        equippedCosmeticIds: hooks!.getEquippedCosmeticIds(),
        profile: {
          dots: hooks!.getDots(),
          tournamentsWon: hooks!.getTournamentsWon(),
          soccerWins: readSoccerWins(),
          cursorColor: hooks!.getCursorColor(),
          accountId: hooks!.getAccountUserId?.() ?? undefined,
        },
      });
      renderSoccerQueue(fmt, variant, 1, cancelSoccerQueue);
    } catch {
      showSoccerToast("Could not connect to matchmaking.");
    }
  })();
}

export function cancelSoccerQueue(): void {
  queueFormat = null;
  queueVariant = null;
  mpClient.send({ type: "soccer_unqueue" });
  renderFormatSelect((fmt, v) => beginSoccerCompeteQueue(fmt, v));
}

export function startSoccerFromMp(): void {
  origin = mpClient.room?.soccerCompete ? "compete" : "multiplayer";
  activeVariant = mpClient.room?.soccerVariant ?? activeVariant;
  beginActiveSession();
}

export function prepareLocalSoccer(names: string[]): void {
  origin = "local";
  localDraftNames = names.slice();
  const ids = names.map((_, i) => `L${i}`);
  const forced = assignRandomTeams(ids);
  localDraftTeams = ids.map((id) => (forced[id] as SoccerTeam) ?? "blue");
  const fmt: SoccerFormat = names.length <= 2 ? "1v1" : "2v2";
  document.getElementById("soccerScreen")?.classList.remove("hidden");
  hooks?.showEl(document.getElementById("soccerScreen"));
  renderVariantSelect(fmt, (variant) => {
    localDraftVariant = variant;
    activeVariant = variant;
    prepareLocalSoccerRefresh();
  });
}

function applyLocalDraftPick(playerId: string | null, team: SoccerTeam | "random"): void {
  const ids = localDraftNames.map((_, i) => `L${i}`);
  if (team === "random") {
    const next = assignRandomTeams(ids);
    localDraftTeams = ids.map((id) => next[id] ?? "blue");
    return;
  }
  const idx = playerId ? ids.indexOf(playerId) : -1;
  if (idx >= 0) localDraftTeams[idx] = team;
}

function prepareLocalSoccerRefresh(): void {
  const names = localDraftNames;
  const ids = names.map((_, i) => `L${i}`);
  renderTeamSelect({
    players: names.map((n, i) => ({ id: ids[i]!, name: n, team: localDraftTeams[i] ?? "blue" })),
    hostCanStart: true,
    perPlayer: true,
    onPick: (t) => {
      applyLocalDraftPick(null, t);
      prepareLocalSoccerRefresh();
    },
    onPickPlayer: (id, t) => {
      applyLocalDraftPick(id, t);
      prepareLocalSoccerRefresh();
    },
    onStart: () => startLocalSoccerMatch(names, localDraftTeams),
  });
}

function startLocalSoccerMatch(names: string[], teams: SoccerTeam[]): void {
  hideOverlay();
  hideSoccerScreens();
  origin = "local";
  const { w, h } = hooks?.getCanvasSize() ?? { w: 800, h: 450 };
  localZones = computeLocalZones(w, h, names.length as 2 | 3 | 4);
  const cfg = soccerVariantConfig(localDraftVariant);
  localPlayers = names.map((name, i) => {
    const team = teams[i] ?? (i % 2 === 0 ? "blue" : "orange");
    const z = localZones[i]!;
    const kx = team === "blue" ? 0.28 : 0.72;
    return {
      id: `L${i}`,
      name,
      team,
      x: cfg.w * kx,
      y: cfg.h * 0.5,
      aimX: cfg.w * kx,
      aimY: cfg.h * 0.5,
      vx: 0,
      vy: 0,
      prevX: cfg.w * kx,
      prevY: cfg.h * 0.5,
      lastMoveAt: performance.now(),
      zone: z,
    };
  });
  activeVariant = localDraftVariant;
  localSim = new SoccerSim({ allowTeamChange: true, compete: false, variant: localDraftVariant });
  localSim.setPlayers(localInputs());
  localSim.startCountdown(false);
  snapPlayersToKickoff(localSim.snapshot());
  localAfkWarned.clear();
  beginActiveSession();
  mountLocalZones();
}

function beginActiveSession(): void {
  if (!hooks) return;
  active = true;
  endedShown = false;
  localPaused = false;
  lastGoalId = 0;
  lastMsgKey = "";
  lastBanner = "";
  lastCountdownShown = "";
  lastSnapPhase = "";
  lastKickoffSyncedGoal = 0;
  hooks.initGame("soccer");
  hooks.hideReturn();
  hideSoccerScreens();
  hideOverlay();
  document.body.classList.add("soccer-active");
  showSoccerMenuBtn(() => openMenu());
  resumeSoccerAudio();
  wireSoccerBoostControls();
  if (origin !== "local") {
    if (reconnectTimer) clearInterval(reconnectTimer);
    reconnectTimer = setInterval(() => {
      if (!active || origin === "local") return;
      if (!mpClient.connected && resumeToken) trySoccerReconnect();
    }, 2500);
  }
}

export function stopSoccer(back: "hub" | "mp" | "local" | "none" = "hub"): void {
  active = false;
  localSim = null;
  netSnap = null;
  snapLerp = 1;
  snapLerpFrom = null;
  localPlayers = [];
  steer.clear();
  localPaused = false;
  endedShown = false;
  queueFormat = null;
  queueVariant = null;
  pcBoostHeld = false;
  mobileBoostOn = false;
  boostWireActive = false;
  unwireSoccerBoostControls();
  unmountLocalZones();
  hideOverlay();
  hideSoccerMenuBtn();
  hideSoccerReplaySkipBtn();
  document.body.classList.remove("soccer-active");
  hooks?.stopPlaying();
  if (reconnectTimer) {
    clearInterval(reconnectTimer);
    reconnectTimer = null;
  }
  if (back === "hub") hooks?.openGameHub();
  else if (back === "mp") hooks?.openGameHub();
  else if (back === "local") hooks?.openLocalSetup();
}

export function handleSoccerReturn(): boolean {
  if (queueFormat) {
    cancelSoccerQueue();
    return true;
  }
  if (isSoccerScreenVisibleSafe()) {
    hideSoccerScreens();
    hooks?.openGameHub();
    return true;
  }
  if (active) return true;
  return false;
}

function isSoccerScreenVisibleSafe(): boolean {
  const el = document.getElementById("soccerScreen");
  return !!el && !el.classList.contains("hidden");
}

export function tickSoccer(dt: number): void {
  if (!active) return;
  if (origin === "local" && localSim) {
    if (localPaused) return;
    applyLocalSteer(dt);
    checkLocalAfk();
    syncBoostWire();
    const events = localSim.step(dt, localInputs());
    syncLocalFromSim();
    playEvents(events, currentView());
    const snap = localSim.snapshot();
    handleClientPhase(snap);
    refreshMobileBoostBtn();
    return;
  }
  if (origin !== "local") {
    const phase = netSnap?.phase;
    if (phase === "playing" || phase === "overtime") {
      sendOnlinePos();
      syncBoostWire();
    }
    if (snapLerp < 1) snapLerp = Math.min(1, snapLerp + dt * 24);
    refreshMobileBoostBtn();
  }
}

export function renderSoccer(
  ctx: CanvasRenderingContext2D,
  canvasW: number,
  canvasH: number,
): void {
  if (!active) return;
  ctx.save();
  ctx.fillStyle = "#071018";
  ctx.fillRect(0, 0, canvasW, canvasH);
  ctx.restore();
  const snap = currentSnap();
  if (!snap) {
    ctx.save();
    ctx.font = '700 12px "Press Start 2P", monospace';
    ctx.fillStyle = "rgba(255,255,255,0.65)";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("Loading match…", canvasW / 2, canvasH / 2);
    ctx.restore();
    return;
  }
  const view = computeSoccerView(canvasW, canvasH, localTeam, origin === "local", getActiveVariant());
  renderSoccerField(ctx, view);
  const simT = localSim?.now() ?? 0;
  renderSoccerPickups(ctx, view, snap.pickups ?? [], simT);
  const ball = snap.phase === "replay" && snap.replay ? interpolateReplayBall(snap) : snap.ball;
  renderSoccerBall(ctx, view, ball.x, ball.y, snap.phase === "replay", ball.angle ?? 0);
  const countdown = snap.phase === "countdown" || snap.phase === "overtime_countdown";
  const livePlay = snap.phase === "playing" || snap.phase === "overtime";
  const displayPlayers = livePlay ? displayPlayersForRender(snap) : snap.players;
  for (const p of displayPlayers) {
    const pos = snap.phase === "replay" ? interpolateReplayPlayer(snap, p.id) ?? p : p;
    const color = p.team === "blue" ? SOCCER_TEAM_BLUE : SOCCER_TEAM_ORANGE;
    const isLocalOnline =
      origin !== "local" && livePlay && p.id === mpClient.playerId && hooks;
    if (countdown && origin !== "local") {
      const s = worldToScreen(pos.x, pos.y, view);
      hooks?.drawCursorAt(ctx, s.x, s.y, color);
      hooks?.drawPlayerName(ctx, s.x, s.y, p.name, color);
      continue;
    }
    if (isLocalOnline && getActiveVariant() !== "mega") {
      const mouse = hooks.getMouse();
      const clamped = clampLocalCursorScreen(mouse.x, mouse.y, snap.ball, view);
      hooks.drawCursorAt(ctx, clamped.x, clamped.y, color);
      hooks.drawPlayerName(ctx, clamped.x, clamped.y, p.name, color);
      continue;
    }
    const s = worldToScreen(pos.x, pos.y, view);
    hooks?.drawCursorAt(ctx, s.x, s.y, color);
    hooks?.drawPlayerName(ctx, s.x, s.y, p.name, color);
    renderSoccerBoostRing(ctx, s.x, s.y, p.boostCharge ?? 1, !!p.boostActive, view);
  }
  if (origin === "local") drawZoneHints(ctx, canvasW, canvasH);
  renderSoccerHud(ctx, canvasW, canvasH, snap, view);
  renderSoccerBoostHint(ctx, canvasW, canvasH, view);
  if (snap.phase === "replay") {
    const awaiting =
      origin === "local"
        ? localReplayAwaiting
        : snap.replayAwaitingNames ?? [];
    renderSoccerReplaySkip(ctx, canvasW, canvasH, awaiting);
  }
  if (netStatusText) {
    ctx.save();
    ctx.font = '700 11px "Press Start 2P", monospace';
    ctx.fillStyle = "#ffcc66";
    ctx.textAlign = "center";
    ctx.fillText(netStatusText, canvasW / 2, canvasH * 0.16);
    ctx.restore();
  }
}

let netStatusText = "";

function displayPlayersForRender(snap: SoccerSnapshot): SoccerSnapshot["players"] {
  if (snapLerp >= 1 || !snapLerpFrom?.length) return snap.players;
  const t = snapLerp;
  return snap.players.map((p) => {
    const from = snapLerpFrom!.find((f) => f.id === p.id);
    if (!from) return p;
    return {
      ...p,
      x: from.x + (p.x - from.x) * t,
      y: from.y + (p.y - from.y) * t,
    };
  });
}

function currentSnap(): SoccerSnapshot | null {
  if (origin === "local") return localSim?.snapshot() ?? null;
  return netSnap;
}

function getActiveVariant(): SoccerVariantId {
  if (localSim) return localSim.variant;
  if (netSnap?.variant) return netSnap.variant;
  if (mpClient.room?.soccerVariant) return mpClient.room.soccerVariant;
  return activeVariant;
}

function activeFieldCfg() {
  return localSim?.cfg ?? soccerVariantConfig(getActiveVariant());
}

function currentView(): SoccerView {
  const { w, h } = hooks?.getCanvasSize() ?? { w: 800, h: 450 };
  return computeSoccerView(w, h, localTeam, origin === "local", getActiveVariant());
}

function localBoostInput(): boolean {
  if (getActiveVariant() !== "mega") return false;
  if (origin === "local") return pcBoostHeld || mobileBoostOn;
  return false;
}

function localInputs(): SoccerPlayerInput[] {
  const boost = localBoostInput();
  const mega = getActiveVariant() === "mega";
  return localPlayers.map((p) => ({
    id: p.id,
    name: p.name,
    team: p.team,
    x: mega ? p.aimX : p.x,
    y: mega ? p.aimY : p.y,
    vx: p.vx,
    vy: p.vy,
    connected: true,
    lastMoveAt: p.lastMoveAt / 1000,
    boost,
  }));
}

function syncLocalFromSim(): void {
  if (!localSim) return;
  const live = localSim.phase === "playing" || localSim.phase === "overtime";
  for (const sp of localSim.players.values()) {
    const lp = localPlayers.find((p) => p.id === sp.id);
    if (!lp) continue;
    lp.team = sp.team;
    if (localSim.phase === "countdown" || localSim.phase === "overtime_countdown") {
      lp.x = sp.x;
      lp.y = sp.y;
      lp.aimX = sp.x;
      lp.aimY = sp.y;
      lp.vx = 0;
      lp.vy = 0;
    } else if (live) {
      lp.x = sp.x;
      lp.y = sp.y;
      lp.vx = sp.vx;
      lp.vy = sp.vy;
    }
  }
}

let localAfkWarned = new Set<string>();

function checkLocalAfk(): void {
  if (!localSim || (localSim.phase !== "playing" && localSim.phase !== "overtime")) return;
  const now = performance.now();
  for (const p of localPlayers) {
    const idle = (now - p.lastMoveAt) / 1000;
    if (idle >= 60) {
      localSim.forfeit(p.team, "AFK");
      return;
    }
    if (idle >= 45 && !localAfkWarned.has(p.id)) {
      localAfkWarned.add(p.id);
      showSoccerToast("Move! The other team wins in 15s if you stay still.");
    }
    if (idle < 45) localAfkWarned.delete(p.id);
  }
}

function applyLocalSteer(dt: number): void {
  if (
    localSim &&
    (localSim.phase === "countdown" || localSim.phase === "overtime_countdown")
  ) {
    return;
  }
  for (const s of steer.values()) {
    const p = localPlayers[s.index];
    if (!p) continue;
    p.vx = (p.x - p.prevX) / Math.max(dt, 1 / 120);
    p.vy = (p.y - p.prevY) / Math.max(dt, 1 / 120);
    p.prevX = p.x;
    p.prevY = p.y;
  }
}

function clampLocalCursorScreen(
  screenX: number,
  screenY: number,
  ball: SoccerSnapshot["ball"],
  view: SoccerView,
): { x: number; y: number } {
  const world = screenToWorld(screenX, screenY, view);
  const cfg = activeFieldCfg();
  const sep = separateCursorFromBall(world.x, world.y, ball.x, ball.y, cfg.cursorR, cfg.ballR);
  return worldToScreen(sep.x, sep.y, view);
}

function sendOnlinePos(): void {
  if (!hooks || !mpClient.connected) return;
  const mouse = hooks.getMouse();
  const view = currentView();
  const world = screenToWorld(mouse.x, mouse.y, view);
  const cfg = activeFieldCfg();
  mpClient.sendPos(world.x / cfg.w, world.y / cfg.h, Date.now() % 1_000_000);
}

function onlineBoostActive(): boolean {
  return getActiveVariant() === "mega" && (pcBoostHeld || mobileBoostOn);
}

function syncBoostWire(): void {
  if (!active || getActiveVariant() !== "mega") return;
  if (origin === "local") return;
  const next = onlineBoostActive();
  if (next === boostWireActive) return;
  boostWireActive = next;
  if (mpClient.connected) mpClient.send({ type: "soccer_boost", active: next });
}

function wireSoccerBoostControls(): void {
  unwireSoccerBoostControls();
  const onDown = (e: MouseEvent) => {
    if (!active || e.button !== 0) return;
    pcBoostHeld = true;
    syncBoostWire();
  };
  const onUp = (e: MouseEvent) => {
    if (e.button !== 0) return;
    pcBoostHeld = false;
    syncBoostWire();
  };
  window.addEventListener("mousedown", onDown);
  window.addEventListener("mouseup", onUp);
  boostListeners = () => {
    window.removeEventListener("mousedown", onDown);
    window.removeEventListener("mouseup", onUp);
  };
  refreshMobileBoostBtn();
}

function unwireSoccerBoostControls(): void {
  boostListeners?.();
  boostListeners = null;
  boostBtn?.classList.add("hidden");
}

function refreshMobileBoostBtn(): void {
  const mega = active && getActiveVariant() === "mega" && isSoccerTouchBoostUi();
  if (!mega) {
    boostBtn?.classList.add("hidden");
    return;
  }
  if (!boostBtn) {
    boostBtn = document.createElement("button");
    boostBtn.type = "button";
    boostBtn.id = "soccerBoostBtn";
    boostBtn.className = "soccerBoostBtn hidden";
    boostBtn.textContent = "BOOST";
    boostBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      mobileBoostOn = !mobileBoostOn;
      boostBtn?.classList.toggle("soccerBoostBtn--on", mobileBoostOn);
      syncBoostWire();
    });
    document.body.appendChild(boostBtn);
  }
  boostBtn.classList.remove("hidden");
  boostBtn.classList.toggle("soccerBoostBtn--on", mobileBoostOn);
}

function playEvents(events: Array<{ kind: string; x: number; y: number; intensity: number; text?: string }>, view: SoccerView): void {
  for (const ev of events) {
    const vx = worldToViewNorm(ev.x, ev.y, view).x;
    if (ev.kind === "hit") playSoccerHit(vx, ev.intensity);
    else if (ev.kind === "wall") playSoccerWall(vx, ev.intensity);
    else if (ev.kind === "post") playSoccerPost(vx);
    else if (ev.kind === "goal") playSoccerGoal();
    else if (ev.kind === "time_warn") {
      if (ev.text === "GO") playSoccerCountdown("go");
      else if (ev.text === "1" || ev.text === "2" || ev.text === "3") playSoccerCountdown(Number(ev.text));
      else playSoccerTimeWarn();
    }     else if (ev.kind === "pickup_max") playSoccerPickupMax();
    else if (ev.kind === "pickup_small") playSoccerPickupSmall();
    else if (ev.kind === "save" || ev.kind === "shot" || ev.kind === "great_shot" || ev.kind === "close_call" || ev.kind === "overtime") {
      playSoccerMessage(ev.kind);
    }
  }
}

function snapPlayersToKickoff(snap: SoccerSnapshot): void {
  if (origin === "local") {
    for (const lp of localPlayers) {
      const sp = snap.players.find((p) => p.id === lp.id);
      if (!sp) continue;
      lp.x = sp.x;
      lp.y = sp.y;
      lp.aimX = sp.x;
      lp.aimY = sp.y;
      lp.vx = 0;
      lp.vy = 0;
      lp.prevX = sp.x;
      lp.prevY = sp.y;
    }
    return;
  }
  const me = snap.players.find((p) => p.id === mpClient.playerId);
  if (!me || !hooks) return;
  const view = currentView();
  const s = worldToScreen(me.x, me.y, view);
  hooks.setMouse(s.x, s.y);
}

function handleClientPhase(snap: SoccerSnapshot): void {
  const countdown =
    snap.phase === "countdown" || snap.phase === "overtime_countdown";
  const wasCountdown =
    lastSnapPhase === "countdown" || lastSnapPhase === "overtime_countdown";

  if (countdown) {
    if (
      lastSnapPhase === "" ||
      lastSnapPhase !== snap.phase ||
      snap.lastGoalId !== lastKickoffSyncedGoal
    ) {
      snapPlayersToKickoff(snap);
      lastKickoffSyncedGoal = snap.lastGoalId;
    }
  } else if (
    wasCountdown &&
    (snap.phase === "playing" || snap.phase === "overtime")
  ) {
    snapPlayersToKickoff(snap);
  }
  lastSnapPhase = snap.phase;
  if (snap.lastGoalId !== lastGoalId && snap.lastGoalId > 0) {
    lastGoalId = snap.lastGoalId;
  }
  const cd = snap.goVisible ? "GO" : String(Math.ceil(snap.countdown));
  if ((snap.phase === "countdown" || snap.phase === "overtime_countdown") && cd !== lastCountdownShown) {
    lastCountdownShown = cd;
  }
  const msg = snap.messages[snap.messages.length - 1];
  if (msg) {
    const key = `${msg.kind}:${msg.text}:${Math.floor(msg.until)}`;
    if (key !== lastMsgKey) lastMsgKey = key;
  }
  if (snap.timeBanner && snap.timeBanner.text !== lastBanner) lastBanner = snap.timeBanner.text;
  const me = snap.players.find((p) => p.id === mpClient.playerId) ?? snap.players[0];
  if (me && origin !== "local") localTeam = me.team;
  if (origin === "local" && localPlayers[0]) localTeam = localPlayers[0].team;
  if (snap.phase === "replay" && localReplayAwaiting.length === 0 && origin === "local") {
    localReplayAwaiting = localPlayers.map((p) => p.name);
  }
  if (snap.phase === "replay") {
    showSoccerReplaySkipBtn(() => trySoccerSkipReplay());
  } else {
    hideSoccerReplaySkipBtn();
    replaySkipSent = false;
    localReplayAwaiting = [];
  }
  if (snap.phase === "ended" && !endedShown) {
    endedShown = true;
    showEnd(snap);
  }
}

function wireFromState(state: SoccerStateWire): SoccerSnapshot {
  activeVariant = parseSoccerVariant(state.variant ?? mpClient.room?.soccerVariant);
  refreshMobileBoostBtn();
  return {
    phase: state.phase as SoccerSnapshot["phase"],
    variant: activeVariant,
    ball: {
      ...state.ball,
      spin: state.ball.spin ?? 0,
      angle: state.ball.angle ?? 0,
    },
    players: state.players.map((p) => ({
      id: p.id,
      name: p.name,
      team: p.team,
      x: p.x,
      y: p.y,
      vx: p.vx,
      vy: p.vy,
      connected: p.connected,
      lastMoveAt: 0,
      goals: p.goals,
      shots: p.shots,
      saves: p.saves,
      touches: p.touches,
      posts: p.posts,
      boostCharge: p.boostCharge ?? 1,
      boostActive: !!p.boostActive,
      desiredX: p.x,
      desiredY: p.y,
      prevX: p.x,
      prevY: p.y,
    })),
    pickups: (state.pickups ?? []).map((p) => ({ ...p })),
    scoreBlue: state.scoreBlue,
    scoreOrange: state.scoreOrange,
    timeLeft: state.timeLeft,
    overtimeTime: state.overtimeTime,
    countdown: state.countdown,
    goVisible: state.goVisible,
    showFiveMinutes: state.showFiveMinutes,
    fiveMinutesAtTop: state.fiveMinutesAtTop,
    lastGoalTeam: state.lastGoalTeam,
    lastGoalId: state.lastGoalId,
    events: [],
    messages: state.messages as SoccerSnapshot["messages"],
    timeBanner: state.timeBanner,
    replay: state.replay,
    replayT: state.replayT,
    replayAwaitingNames: state.replayAwaitingNames,
    winnerTeam: state.winnerTeam,
    forfeitReason: state.forfeitReason,
    afkWarned: state.afkWarned,
    paused: state.paused,
  };
}

function onMpMessage(msg: MpServerMessage): void {
  if (msg.type === "soccer_queue_status") {
    const v = msg.variant ?? "normal";
    if (queueFormat === msg.format && (queueVariant ?? "normal") === v) {
      updateSoccerQueueCount(msg.format, v, msg.searching);
    }
    return;
  }
  if (msg.type === "soccer_resume_token") {
    resumeToken = msg.token;
    resumePlayerId = msg.playerId;
    return;
  }
  if (msg.type === "game_start" && msg.mode === "soccer") {
    queueFormat = null;
    startSoccerFromMp();
    return;
  }
  if (msg.type === "soccer_state") {
    if (netSnap?.players?.length) {
      snapLerpFrom = netSnap.players.map((p) => ({ ...p }));
    } else {
      snapLerpFrom = null;
    }
    netSnap = wireFromState(msg.state);
    snapLerp = 0;
    if (msg.events?.length) playEvents(msg.events, currentView());
    handleClientPhase(netSnap);
    return;
  }
  if (msg.type === "soccer_afk_warn") {
    showSoccerToast(`Move! Opponent wins in ${msg.secondsLeft}s if you stay AFK.`);
    return;
  }
  if (msg.type === "soccer_player_net") {
    if (msg.status === "reconnecting") netStatusText = "Player reconnecting…";
    else netStatusText = "";
    return;
  }
  if (msg.type === "soccer_match_closed") {
    if (active && !endedShown) {
      stopSoccer(origin === "local" ? "local" : "hub");
    }
    return;
  }
  if (msg.type === "soccer_rematch_update") {
    const need = mpClient.room?.players.length ?? 0;
    setSoccerRematchHint(`Rematch ${msg.accepted.length}/${need}${msg.declined.length ? " — someone declined" : ""}`);
    return;
  }
  if (msg.type === "room_state" && msg.room.phase === "soccer_team_select" && !active) {
    origin = "multiplayer";
    activeVariant = msg.room.soccerVariant ?? "normal";
    renderTeamSelect({
      players: msg.room.players.map((p) => ({
        id: p.id,
        name: p.name,
        team: (msg.room.soccerTeams?.[p.id] as SoccerTeam) ?? null,
        isLocal: p.id === mpClient.playerId,
      })),
      hostCanStart: mpClient.isHost(),
      onPick: (team) => mpClient.send({ type: "soccer_set_team", team }),
      onStart: () => mpClient.send({ type: "soccer_start" }),
    });
    if (mpClient.isHost()) showMpHostVariantBar();
  }
}

function showMpHostVariantBar(): void {
  renderSoccerHostVariant(activeVariant, (variant) => {
    activeVariant = variant;
    mpClient.send({ type: "soccer_set_variant", variant });
    showMpHostVariantBar();
  });
}

function openMenu(): void {
  if (!active || endedShown) return;
  if (origin === "local") {
    localPaused = true;
    if (localSim) localSim.paused = true;
  }
  renderSoccerPause({
    allowTeamChange: origin !== "compete" && !mpClient.room?.soccerCompete,
    localRoster:
      origin === "local"
        ? localPlayers.map((p) => ({ id: p.id, name: p.name, team: p.team }))
        : undefined,
    onResume: () => {
      localPaused = false;
      if (localSim) localSim.paused = false;
    },
    onChangeTeam: (team, playerId) => {
      localPaused = false;
      if (localSim) {
        localSim.paused = false;
        const me = localPlayers.find((p) => p.id === playerId) ?? localPlayers[0];
        if (me) {
          localSim.changeTeam(me.id, team);
          me.team = team;
          const sp = localSim.players.get(me.id);
          me.x = sp?.x ?? me.x;
          me.y = sp?.y ?? me.y;
          me.aimX = me.x;
          me.aimY = me.y;
          mountLocalZones();
        }
      } else {
        mpClient.send({ type: "soccer_change_team", team });
      }
    },
    onLeave: () => {
      if (origin !== "local") mpClient.send({ type: "soccer_leave" });
      stopSoccer(origin === "local" ? "local" : "hub");
    },
  });
}

function showEnd(snap: SoccerSnapshot): void {
  hideSoccerMenuBtn();
  const winners = snap.players.filter((p) => p.team === snap.winnerTeam);
  const localWin =
    origin === "local"
      ? !!winners.length
      : winners.some((p) => p.id === mpClient.playerId);
  if (localWin && origin === "compete") writeSoccerWin();
  const face = winners[0] ?? snap.players[0];
  const winner: SoccerWinnerCard = {
    name: winners.length > 1 ? `${winners.map((w) => w.name).join(" & ")}` : face?.name ?? "Winner",
    team: snap.winnerTeam ?? "blue",
    dots: hooks?.getDots() ?? 0,
    tournamentsWon: hooks?.getTournamentsWon() ?? 0,
    soccerWins: readSoccerWins(),
    photoUrl: hooks?.getProfilePhotoUrl?.() ?? null,
    bannerUrl: hooks?.getBannerUrl?.() ?? null,
    cursorColor: face?.team === "blue" ? SOCCER_TEAM_BLUE : SOCCER_TEAM_ORANGE,
  };
  renderSoccerWinnerThenResults({
    winner,
    stats: snap.players.map((p) => ({
      id: p.id,
      name: p.name,
      team: p.team,
      goals: p.goals,
      shots: p.shots,
      saves: p.saves,
      touches: p.touches,
      posts: p.posts,
    })),
    onPlayAgain: () => {
      if (origin === "compete") {
        stopSoccer("none");
        hideSoccerScreens();
        document.getElementById("soccerScreen")?.classList.remove("hidden");
        hooks?.showEl(document.getElementById("soccerScreen"));
        renderFormatSelect((fmt, v) => beginSoccerCompeteQueue(fmt, v));
      } else if (origin === "local") {
        const names = localDraftNames;
        stopSoccer("none");
        prepareLocalSoccer(names);
      } else {
        stopSoccer("hub");
      }
    },
    onRematch: () => {
      if (origin === "local") {
        hideOverlay();
        endedShown = false;
        if (localSim) {
          localSim = new SoccerSim({ allowTeamChange: true, compete: false, variant: localDraftVariant });
          localSim.setPlayers(localInputs());
          localSim.startCountdown(false);
        }
        showSoccerMenuBtn(() => openMenu());
      } else {
        mpClient.send({ type: "soccer_rematch_request" });
        setSoccerRematchHint("Rematch requested…");
      }
    },
    onLeave: () => stopSoccer(origin === "local" ? "local" : "hub"),
  });
}

function interpolateReplayBall(snap: SoccerSnapshot) {
  return snap.ball;
}
function interpolateReplayPlayer(snap: SoccerSnapshot, id: string) {
  return snap.players.find((p) => p.id === id) ?? null;
}

function mountLocalZones(): void {
  unmountLocalZones();
  const root = document.createElement("div");
  root.id = "soccerTouchZones";
  root.className = "soccerTouchZones";
  localZones.forEach((z, i) => {
    const el = document.createElement("div");
    el.className = `soccerTouchZone soccerTouchZone--${localPlayers[i]?.team ?? "blue"}`;
    el.style.left = `${(z.x / (hooks?.getCanvasSize().w || 1)) * 100}%`;
    el.style.top = `${(z.y / (hooks?.getCanvasSize().h || 1)) * 100}%`;
    el.style.width = `${(z.w / (hooks?.getCanvasSize().w || 1)) * 100}%`;
    el.style.height = `${(z.h / (hooks?.getCanvasSize().h || 1)) * 100}%`;
    el.innerHTML = `<span>${localPlayers[i]?.name ?? `P${i + 1}`}</span>`;
    el.addEventListener("pointerdown", (e) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      e.preventDefault();
      el.setPointerCapture(e.pointerId);
      steer.set(e.pointerId, { index: i, lastX: e.clientX, lastY: e.clientY });
    });
    el.addEventListener("pointermove", (e) => {
      const s = steer.get(e.pointerId);
      const p = s ? localPlayers[s.index] : null;
      if (!s || !p || !hooks) return;
      const raw = hooks.convertClientDelta?.(e.clientX - s.lastX, e.clientY - s.lastY) ?? {
        dx: e.clientX - s.lastX,
        dy: e.clientY - s.lastY,
      };
      s.lastX = e.clientX;
      s.lastY = e.clientY;
      const view = currentView();
      const scale = soccerScale(view);
      const cfg = localSim?.cfg ?? soccerVariantConfig(getActiveVariant());
      const mega = cfg.speedCapped;
      const dx = raw.dx / scale;
      const dy = raw.dy / scale;
      if (mega) {
        p.aimX = Math.max(cfg.cursorR, Math.min(cfg.w - cfg.cursorR, p.aimX + dx));
        p.aimY = Math.max(cfg.cursorR, Math.min(cfg.h - cfg.cursorR, p.aimY + dy));
      } else {
        p.x = Math.max(cfg.cursorR, Math.min(cfg.w - cfg.cursorR, p.x + dx));
        p.y = Math.max(cfg.cursorR, Math.min(cfg.h - cfg.cursorR, p.y + dy));
        p.aimX = p.x;
        p.aimY = p.y;
      }
      p.lastMoveAt = performance.now();
    });
    const end = (e: PointerEvent) => steer.delete(e.pointerId);
    el.addEventListener("pointerup", end);
    el.addEventListener("pointercancel", end);
    root.appendChild(el);
  });
  document.body.appendChild(root);
  touchRoot = root;
}

function unmountLocalZones(): void {
  touchRoot?.remove();
  touchRoot = null;
  steer.clear();
}

function drawZoneHints(ctx: CanvasRenderingContext2D, cw: number, ch: number): void {
  ctx.save();
  ctx.globalAlpha = 0.18;
  ctx.font = '700 12px "Press Start 2P", monospace';
  ctx.textAlign = "center";
  for (const p of localPlayers) {
    ctx.fillStyle = p.team === "blue" ? SOCCER_TEAM_BLUE : SOCCER_TEAM_ORANGE;
    ctx.fillRect(p.zone.x, p.zone.y, 4, p.zone.h);
    ctx.fillText(p.name, p.zone.cx, p.zone.y + 18);
  }
  ctx.restore();
  void cw;
  void ch;
}

export function soccerLocalPointer(clientX: number, clientY: number, canvas: HTMLCanvasElement): void {
  if (!active || origin === "local" || !hooks) return;
  const rect = canvas.getBoundingClientRect();
  const x = ((clientX - rect.left) / rect.width) * canvas.width;
  const y = ((clientY - rect.top) / rect.height) * canvas.height;
  hooks.setMouse(x, y);
}

export function trySoccerSkipReplay(): boolean {
  if (!active) return false;
  const snap = currentSnap();
  if (!snap || snap.phase !== "replay") return false;
  if (origin === "local") {
    localReplayAwaiting = [];
    localSim?.endReplayEarly();
    return true;
  }
  if (replaySkipSent) return true;
  replaySkipSent = true;
  mpClient.send({ type: "soccer_skip_replay" });
  return true;
}

export function trySoccerReconnect(): void {
  if (!resumeToken || !resumePlayerId || origin === "local") return;
  netStatusText = "Reconnecting…";
  void mpClient.connect().then(() => {
    mpClient.send({
      type: "soccer_rejoin",
      playerId: resumePlayerId,
      token: resumeToken,
      name: hooks?.getPlayerName() || "Player",
    });
  });
}

function readSoccerWins(): number {
  try {
    return Math.max(0, Number(JSON.parse(localStorage.getItem(STATS_KEY) || "{}").wins) || 0);
  } catch {
    return 0;
  }
}

function writeSoccerWin(): void {
  const wins = readSoccerWins() + 1;
  localStorage.setItem(STATS_KEY, JSON.stringify({ wins }));
}

export function bindSoccerFormatPicker(): void {
  renderFormatSelect((fmt, v) => beginSoccerCompeteQueue(fmt, v));
}
